import React, { useContext, useEffect, useCallback, useState } from 'react';
// eslint-disable-next-line no-unused-vars
import PropTypes from 'prop-types';
import {
    CommunityContext,
    PageContext,
} from '../../../../contexts/ContextCreator';
import CommunityHeartIcon from '../../../Icons/CommunityHeartIcon/CommunityHeartIcon';
import ThumbUpIcon from '../../../Icons/ThumbUpIcon/ThumbUpIcon';
import './CommunityLikeButton.scss';
import {
    emitEngagement,
    objectTypes,
} from '../../../../utilities/getStreamAnalytics';

const CommunityLikeButton = ({
    hash,
    showLabel,
    isCommunityDiscussionType,
}) => {
    // CONSTS ==========================
    const {
        isLoggedIn,
        wrapAuth,
        pageProps: {
            session: { id, name },
        },
    } = useContext(PageContext);
    const { getContent, vote } = useContext(CommunityContext);
    const content = getContent(hash);
    const preloading = content?.preloading;

    if (preloading) content.likes = 0;
    const [isActive, setIsActive] = useState(false);
    const contentIsComment = content?.type === 'community-comment';
    const emptyClass = content.likes === 0 ? 'empty-styling' : '';
    const authMetrics = {
        login_trigger: 'community-like',
        community_content_key: `${content.type}-${content.hash}`,
        community_on_comment: content.type === 'community-comment',
    };

    // FUNCTIONS ======================================
    useEffect(() => {
        setIsActive(content?.userLiked);
    }, [setIsActive, content?.userLiked]);

    const handleClick = useCallback(() => {
        if (!isLoggedIn()) return;
        vote(content.hash, 'like');
        setIsActive(!isActive);
        if (isActive === false) {
            const metricContent = contentIsComment
                ? {
                      foreign_id: `${
                          objectTypes()[content.parentContentType]
                      }:${content.hash}`,
                      comment: {
                          id: `${objectTypes()[content.type]}:${content.hash}`,
                          label: `${objectTypes()[content.parentContentType]}:${
                              content.hash
                          }`,
                      },
                  }
                : {
                      foreign_id: `${objectTypes()[content.type]}:${
                          content.hash
                      }`,
                  };
            emitEngagement(
                { id, name },
                {
                    label: 'like',
                    score: 1,
                    content: metricContent,
                }
            );
        }
    }, [
        isLoggedIn,
        vote,
        isActive,
        setIsActive,
        content.hash,
        content.type,
        id,
        name,
    ]);

    const renderLikeLabels = () => {
        return (
            <>
                {showLabel && content.likes <= 1 && (
                    <p className="button-label">
                        {content.likes !== 0 ? `${content.likes} like` : 'Like'}
                    </p>
                )}
                {showLabel && content.likes > 1 && (
                    <p className="button-label">{content.likes} likes</p>
                )}
            </>
        );
    };

    const renderUpvoteLabels = () => {
        return (
            <>
                {showLabel && content.likes <= 1 && (
                    <p className="button-label thumbsup">
                        {content.likes !== 0
                            ? `${content.likes} upvote`
                            : 'Upvote'}
                    </p>
                )}
                {showLabel && content.likes > 1 && (
                    <p className="button-label thumbsup">
                        {content.likes} upvotes
                    </p>
                )}
            </>
        );
    };

    // RENDER ======================================
    return wrapAuth(
        `community-like-${content.hash}`,
        <>
            <button
                type="button"
                className={`component-Community-ReactionBar-CommunityLikeButton ${
                    contentIsComment ? 'thumbsup' : ''
                } ${emptyClass}`}
                onClick={handleClick}
            >
                {!contentIsComment && (
                    <>
                        <CommunityHeartIcon isActive={isActive} />
                        <div
                            {...(isCommunityDiscussionType && {
                                itemProp: 'interactionStatistic',
                                itemScope: 'itemscope',
                                itemType:
                                    'https://schema.org/InteractionCounter',
                            })}
                        >
                            {isCommunityDiscussionType && (
                                <>
                                    <link
                                        itemProp="interactionType"
                                        href="https://schema.org/LikeAction"
                                    />
                                    <meta
                                        itemProp="interactionType"
                                        content="https://schema.org/LikeAction"
                                    />
                                    <meta
                                        itemProp="userInteractionCount"
                                        content={content.likes}
                                    />
                                </>
                            )}
                            {renderLikeLabels()}
                        </div>
                    </>
                )}
                {contentIsComment && (
                    <>
                        <ThumbUpIcon isActive={isActive} />
                        {renderUpvoteLabels()}
                    </>
                )}
            </button>
        </>,
        authMetrics
    );
};

CommunityLikeButton.propTypes = {
    hash: PropTypes.string.isRequired,
    showLabel: PropTypes.bool,
    isCommunityDiscussionType: PropTypes.bool,
};

CommunityLikeButton.defaultProps = {
    showLabel: false,
    isCommunityDiscussionType: false,
};

CommunityLikeButton.displayName = 'CommunityLikeButton';

export default React.memo(CommunityLikeButton);
